interface StudentData {
  name: string;
  email: string;
  status: string;
  enrollmentDate: string;
  avatar?: {
    url?: string;
    alt?: string;
  };
  progress: {
    packagesCount: number;
    overallProgress: number;
  };
  levelTest?: {
    hasSession: boolean;
    finalLevel?: number;
    finalLevelName?: string;
    status?: string;
  };
}

interface StudentCardActions {
  onEdit?: (studentId: string) => void;
  onDelete?: (studentId: string) => void;
  onToggleLock?: (studentId: string) => void;
}

interface StudentCardProps extends StudentCardActions {
  student: StudentData;
  studentId: string;
  className?: string;
  isLocked?: boolean;
}

import { PersonAvatar } from "@/components/ui/person-avatar";

const StudentCard: React.FC<StudentCardProps> = ({
  student,
  studentId,
  onEdit,
  onDelete,
  onToggleLock,
  className = "",
  isLocked = false,
}) => {
  return (
    <div className={`rounded-lg border bg-white p-6 shadow-sm ${className}`}>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <PersonAvatar
            userId={studentId}
            userName={student.name}
            avatarUrl={student.avatar?.url}
            size="md"
          />
          <div>
            <h3 className="text-lg font-semibold">{student.name}</h3>
            <p className="text-sm text-neutral-600">{student.email}</p>
          </div>
        </div>
        <span className="rounded-full bg-neutral-100 px-3 py-1 text-sm text-neutral-800">
          {student.status}
        </span>
      </div>
      <div className="space-y-3">
        <div className="flex items-center space-x-2">
          <i className="fa-solid fa-book text-neutral-500"></i>
          <span className="text-neutral-600">{student.progress.packagesCount} Package{student.progress.packagesCount !== 1 ? 's' : ''} Purchased</span>
        </div>
        <div className="flex items-center space-x-2">
          <i className="fa-solid fa-chart-line text-neutral-500"></i>
          <span className="text-neutral-600">
            {student.progress.overallProgress > 0 
              ? `${student.progress.overallProgress}% Overall Progress`
              : "No progress yet"}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <i className="fa-regular fa-calendar text-neutral-500"></i>
          <span className="text-neutral-600">
            Registered: {
              (() => {
                if (!student.enrollmentDate || student.enrollmentDate === "N/A" || student.enrollmentDate.startsWith("0001")) {
                  return "N/A";
                }
                try {
                  const date = new Date(student.enrollmentDate);
                  if (isNaN(date.getTime()) || date.getFullYear() <= 1) {
                    return "N/A";
                  }
                  return date.toLocaleDateString();
                } catch {
                  return "N/A";
                }
              })()
            }
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <i className="fa-solid fa-graduation-cap text-neutral-500"></i>
          <span className="text-neutral-600">
            {student.levelTest?.hasSession ? (
              student.levelTest.finalLevel != null ? (
                `Level Test: Level ${student.levelTest.finalLevel}${student.levelTest.finalLevelName ? ` (${student.levelTest.finalLevelName})` : ''}`
              ) : (
                `Level Test: ${student.levelTest.status || 'In Progress'}`
              )
            ) : (
              "No level test"
            )}
          </span>
        </div>
        <div className="flex space-x-2 pt-2 items-center">
          {onEdit && (
            <button 
              onClick={() => onEdit(studentId)}
              className="rounded-lg bg-neutral-100 p-2 hover:bg-neutral-200"
              aria-label="Edit student"
            >
              <i className="fa-solid fa-pen text-neutral-600"></i>
            </button>
          )}
          {onDelete && (
            <button 
              onClick={() => onDelete(studentId)}
              className="rounded-lg bg-neutral-100 p-2 hover:bg-neutral-200"
              aria-label="Delete student"
            >
              <i className="fa-solid fa-trash text-neutral-600"></i>
            </button>
          )}
          {onToggleLock && (
            <button 
              onClick={() => onToggleLock(studentId)}
              className={`rounded-lg p-2 transition-colors ${
                isLocked 
                  ? 'bg-red-100 hover:bg-red-200' 
                  : 'bg-green-100 hover:bg-green-200'
              }`}
              aria-label={isLocked ? "Unlock student" : "Lock student"}
            >
              <i className={`fa-solid ${isLocked ? 'fa-lock' : 'fa-lock-open'} ${
                isLocked ? 'text-red-600' : 'text-green-600'
              }`}></i>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export { StudentCard };
